// Catalog.js
import React, { useState, useEffect, useCallback } from 'react';
import { db } from '../../firebaseConfig';
import {
  collection,
  addDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  doc
} from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import styles from '../styles/Catalog.module.css';
import backgroundImage from '../../assets/background6.jpg';

const Catalog = () => {
  const [catalogType, setCatalogType] = useState('catalogo');
  const [catalogs, setCatalogs] = useState([]);
  const [plataformas, setPlataformas] = useState([]); // Nueva estado para plataformas
  const [editingItem, setEditingItem] = useState(null);

  const [formData, setFormData] = useState({
    plataforma: '',
    unidad: 'completa',
    pantallas: '',
    descripcion: [],
    calidad: '',
    duracion: '',
    etiqueta: '',
    popular: false,
    precioBaseCOP: '',
    porcentajeDescuentoCOP: '',
    precioConDescuentoCOP: '',
    precioBaseUSD: '',
    porcentajeDescuentoUSD: '',
    precioConDescuentoUSD: ''
  });

  const navigate = useNavigate();

  const handleBack = () => {
    navigate('/home');
  };

  const handleDelete = async (id) => {
    if (window.confirm("¿Estás seguro de que deseas eliminar este elemento del catálogo?")) {
      try {
        await deleteDoc(doc(db, catalogType, id));
        alert("Elemento eliminado correctamente.");
        fetchCatalogs();
      } catch (error) {
        console.error("Error al eliminar:", error);
        alert("Hubo un error al eliminar el elemento.");
      }
    }
  };

  const tipos = [
    { label: 'Catálogo General', value: 'catalogo' },
    { label: 'Catálogo Vendedores', value: 'catalogoVendedores' }
  ];

  const backgroundStyle = {
    minHeight: '100vh',
    width: '100%',
    margin: 0,
    padding: 0,
    backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.60), rgba(0, 0, 0, 0.75)), url(${backgroundImage})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundAttachment: 'fixed',
    backgroundRepeat: 'no-repeat',
    position: 'relative',
    overflowX: 'hidden'
  };

  const unidades = ['completa', 'pantalla'];

  // Nueva función para obtener plataformas desde Firebase
  const fetchPlataformas = useCallback(async () => {
    try {
      const snapshot = await getDocs(collection(db, 'plataformas'));
      const data = snapshot.docs.map(doc => ({ 
        id: doc.id, 
        ...doc.data() 
      }));
      // Ordenar alfabéticamente por nombre
      data.sort((a, b) => a.nombre.localeCompare(b.nombre));
      setPlataformas(data);
    } catch (error) {
      console.error("Error al obtener plataformas:", error);
      // Fallback a plataformas estáticas en caso de error
      setPlataformas([
        { id: 'netflix', nombre: 'Netflix', imagen: '' },
        { id: 'disney', nombre: 'Disney Plus', imagen: '' },
        { id: 'paramount', nombre: 'Paramount', imagen: '' },
        // ... otras plataformas como fallback
      ]);
    }
  }, []);

  const fetchCatalogs = useCallback(async () => {
    const snapshot = await getDocs(collection(db, catalogType));
    const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    setCatalogs(data);
  }, [catalogType]);

  // Cargar plataformas al montar el componente
  useEffect(() => {
    fetchPlataformas();
  }, [fetchPlataformas]);

  useEffect(() => {
    fetchCatalogs();
  }, [fetchCatalogs]);

  const calcularPrecioConDescuento = useCallback((base, descuento) => {
    const b = parseFloat(base) || 0;
    const d = parseFloat(descuento) || 0;
    return (b - (b * d / 100)).toFixed(2);
  }, []);

  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      precioConDescuentoCOP: calcularPrecioConDescuento(prev.precioBaseCOP, prev.porcentajeDescuentoCOP),
      precioConDescuentoUSD: calcularPrecioConDescuento(prev.precioBaseUSD, prev.porcentajeDescuentoUSD)
    }));
  }, [
    formData.precioBaseCOP,
    formData.porcentajeDescuentoCOP,
    formData.precioBaseUSD,
    formData.porcentajeDescuentoUSD,
    calcularPrecioConDescuento
  ]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));
  };

  const handleSave = async () => {
    try {
      const dataToSave = {
        ...formData,
        precioConDescuentoCOP: calcularPrecioConDescuento(formData.precioBaseCOP, formData.porcentajeDescuentoCOP),
        precioConDescuentoUSD: calcularPrecioConDescuento(formData.precioBaseUSD, formData.porcentajeDescuentoUSD)
      };

      if (editingItem) {
        const ref = doc(db, catalogType, editingItem.id);
        await updateDoc(ref, dataToSave);
        setEditingItem(null);
      } else {
        await addDoc(collection(db, catalogType), dataToSave);
      }

      setFormData({
        plataforma: '',
        unidad: 'completa',
        pantallas: '',
        descripcion: [],
        calidad: '',
        duracion: '',
        etiqueta: '',
        popular: false,
        precioBaseCOP: '',
        porcentajeDescuentoCOP: '',
        precioConDescuentoCOP: '',
        precioBaseUSD: '',
        porcentajeDescuentoUSD: '',
        precioConDescuentoUSD: ''
      });

      fetchCatalogs();
      alert('Elemento guardado exitosamente.');
    } catch (err) {
      alert('Error al guardar: ' + err.message);
    }
  };

  const startEditing = (item) => {
    setEditingItem(item);
    setFormData(item);
  };

  return (
    <div style={backgroundStyle}>
      <div className={styles.container}>
        <h2 className={styles.title}>Gestión de Catálogos</h2>

        <select value={catalogType} onChange={(e) => setCatalogType(e.target.value)} className={styles.select}>
          {tipos.map(t => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </select>

        <div className={styles.form}>
          {/* Select modificado para usar plataformas desde Firebase */}
          <select name="plataforma" value={formData.plataforma} onChange={handleChange}>
            <option value="">-- Selecciona una Plataforma --</option>
            {plataformas.map(p => (
              <option key={p.id} value={p.nombre}>{p.nombre}</option>
            ))}
            <option value="Otro">Otro</option>
          </select>

          <select name="unidad" value={formData.unidad} onChange={handleChange}>
            {unidades.map(u => (
              <option key={u} value={u}>{u}</option>
            ))}
          </select>

          <select name="etiqueta" value={formData.etiqueta} onChange={handleChange}>
            <option value="">-- Etiqueta --</option>
            <option value="Premium">Premium</option>
            <option value="Estándar">Estándar</option>
            <option value="Básico">Básico</option>
          </select>

          <input name="calidad" placeholder="Calidad (ej: HD, 4K)" value={formData.calidad} onChange={handleChange} />
          <input name="duracion" placeholder="Duración (ej: 365 días)" value={formData.duracion} onChange={handleChange} />
          <input name="pantallas" type="number" placeholder="Número de pantallas" value={formData.pantallas} onChange={handleChange} />

          <button 
          className={styles.addDescriptionButton}
          onClick={() => {
            const newItem = prompt('Agrega un nuevo detalle a la descripción:');
            if (newItem) {
              setFormData(prev => ({ ...prev, descripcion: [...prev.descripcion, newItem] }));
            }
          }}>
            + Agregar Descripción
          </button>

          <ul>
            {formData.descripcion.map((d, i) => <li key={i}>{d}</li>)}
          </ul>

          <input name="precioBaseCOP" type="number" placeholder="Precio Base (COP)" value={formData.precioBaseCOP} onChange={handleChange} />
          <input name="porcentajeDescuentoCOP" type="number" placeholder="Descuento % COP" value={formData.porcentajeDescuentoCOP} onChange={handleChange} />
          <input name="precioConDescuentoCOP" type="number" placeholder="Precio Final COP" value={formData.precioConDescuentoCOP} readOnly />

          <input name="precioBaseUSD" type="number" placeholder="Precio Base (USD)" value={formData.precioBaseUSD} onChange={handleChange} />
          <input name="porcentajeDescuentoUSD" type="number" placeholder="Descuento % USD" value={formData.porcentajeDescuentoUSD} onChange={handleChange} />
          <input name="precioConDescuentoUSD" type="number" placeholder="Precio Final USD" value={formData.precioConDescuentoUSD} readOnly />

          <label>
            <input type="checkbox" name="popular" checked={formData.popular} onChange={handleChange} />
            Popular
          </label>

          <button className={styles.saveButton} onClick={handleSave}>
            {editingItem ? 'Guardar Cambios' : 'Crear'}
          </button>
        </div>

    <table className={styles.table}>
      <thead>
        <tr>
          <th>Plataforma</th>
          <th>Unidad</th>
          <th>Pantallas</th>
          <th>Calidad</th>
          <th>Etiqueta</th>
          <th>Duración</th>
          <th>Descripción</th>
          <th>Precio COP</th>
          <th>Descuento %</th>
          <th>Final COP</th>
          <th>Precio USD</th>
          <th>Descuento %</th>
          <th>Final USD</th>
          <th>Popular</th>
          <th>Acciones</th>
        </tr>
      </thead>
      <tbody>
        {catalogs.map(item => (
          <tr key={item.id}>
            <td>{item.plataforma}</td>
            <td>{item.unidad}</td>
            <td>{item.pantallas}</td>
            <td>{item.calidad}</td>
            <td>{item.etiqueta}</td>
            <td>{item.duracion}</td>
            <td>
              <ul>
                {Array.isArray(item.descripcion) ? item.descripcion.map((d, i) => <li key={i}>{d}</li>) : item.descripcion}
              </ul>
            </td>
            <td>{item.precioBaseCOP}</td>
            <td>{item.porcentajeDescuentoCOP}</td>
            <td>{item.precioConDescuentoCOP}</td>
            <td>{item.precioBaseUSD}</td>
            <td>{item.porcentajeDescuentoUSD}</td>
            <td>{item.precioConDescuentoUSD}</td>
            <td>{item.popular ? '✅' : '❌'}</td>
            <td>
              <div className={styles.actionButtons}>
                <button className={styles.editButton} onClick={() => startEditing(item)}>Editar</button>
                <button className={styles.deleteButton} onClick={() => handleDelete(item.id)}>Eliminar</button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>

        <div style={{ width: '100%', marginTop: '30px' }}>
          <button onClick={handleBack} className={styles.transparentFullButton}>
            Regresar a Inicio 🐻
          </button>
        </div>
      </div>
    </div>
  );
};

export default Catalog;